import { Stack } from '@trmediaab/zebra';
import type { CheckoutUser } from '@trmediaab/zebra-types/trhub';
import { formatPostalCode } from '@trmediaab/zebra-utils';
import { useEffect, useReducer, useState } from 'react';

import TextButton from '../../components/TextButton';
import { setCountry } from '../../state';
import AddressForm from './AddressForm';
import AddressInfo from './AddressInfo';
import type { NewOrExistingUserWithShippingAddress } from './types';
import {
  isUserWithAddress,
  isUserWithShippingAddress,
} from './validateAddress';

function initState(
  user: Partial<CheckoutUser> | undefined,
): NewOrExistingUserWithShippingAddress {
  const u = user || {};

  if (u.address == null) {
    u.address = {};
  }

  return {
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    mobile: u.mobile || '',
    address: {
      address: u.address.address || '',
      careOf: u.address.careOf || '',
      city: u.address.city || '',
      postalCode: formatPostalCode(u.address.postalCode || ''),
      country: u.address.country || '',
    },
  };
}

export type Action =
  | {
      type:
        | 'firstName'
        | 'lastName'
        | 'address'
        | 'careOf'
        | 'city'
        | 'postalCode'
        | 'mobile'
        | 'country';
      payload: string;
    }
  | {
      type: 'reset';
      payload: NewOrExistingUserWithShippingAddress;
    };

function reducer(state: NewOrExistingUserWithShippingAddress, action: Action) {
  const { type, payload } = action;
  switch (type) {
    case 'firstName':
      return { ...state, firstName: payload };
    case 'lastName':
      return { ...state, lastName: payload };
    case 'address':
      return { ...state, address: { ...state.address, address: payload } };
    case 'careOf':
      return { ...state, address: { ...state.address, careOf: payload } };
    case 'city':
      return { ...state, address: { ...state.address, city: payload } };
    case 'postalCode':
      return {
        ...state,
        address: { ...state.address, postalCode: payload },
      };
    case 'mobile':
      return { ...state, mobile: payload };
    case 'country':
      return { ...state, address: { ...state.address, country: payload } };
    case 'reset':
      return payload;
    default:
      return state;
  }
}

interface AddressProps {
  required?: 'address' | 'shippingAddress';
  user?: CheckoutUser;
  disabled: boolean;
  onChange: (values: NewOrExistingUserWithShippingAddress) => void;
}

const Address = ({ required, user, disabled, onChange }: AddressProps) => {
  const [values, dispatch] = useReducer(reducer, user, initState);
  const [editCurrent, setEditCurrent] = useState(false);

  useEffect(() => {
    onChange(values);
  }, [onChange, values]);

  useEffect(() => {
    setCountry(values.address.country);
  }, [values.address.country]);

  if (required == null) {
    return null;
  }

  if (
    !editCurrent &&
    ((required !== 'shippingAddress' && isUserWithAddress(user)) ||
      (required === 'shippingAddress' && isUserWithShippingAddress(user)))
  ) {
    return (
      <Stack between="3">
        <AddressInfo user={user} />
        <TextButton label="Byt address" onClick={() => setEditCurrent(true)} />
      </Stack>
    );
  }

  return (
    <Stack between="3">
      <AddressForm
        values={values}
        disabled={disabled}
        dispatch={dispatch}
        isShippingAddress={required === 'shippingAddress'}
      />
      {editCurrent && (
        <TextButton
          label="Ångra"
          disabled={disabled}
          onClick={() => {
            setEditCurrent(false);
            dispatch({ type: 'reset', payload: initState(user) });
          }}
        />
      )}
    </Stack>
  );
};

export default Address;
